import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { IconEye, IconCode } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { AgentProfileResponseDto } from '@shared/services/apiService';
import { useStyles } from '../styles';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { MarkdownContent } from '@shared/components/MarkdownEditor';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { TaskFlowPanel } from '@features/Admin/Agent/ui/TaskFlowCanvas';

interface ViewAgentProfileModalProps {
  open: boolean;
  onClose: () => void;
  profile: AgentProfileResponseDto | null;
}

export const ViewAgentProfileModal: React.FC<ViewAgentProfileModalProps> = ({
  open,
  onClose,
  profile,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const [showRaw, setShowRaw] = useState(false);
  const [tab, setTab] = useState(0);
  const [openingLineExpanded, setOpeningLineExpanded] = useState(false);

  useEffect(() => {
    if (open && profile) {
      setOpeningLineExpanded(profile.firstMessageInterruptionsEnabled ?? false);
    }
  }, [open, profile]);

  if (!profile) return null;

  const handleClose = () => {
    setShowRaw(false);
    setTab(0);
    setOpeningLineExpanded(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.modalPaper }}
      sx={{
        '& .MuiDialog-paper': {
          height: { xs: '100%', md: '88vh' },
          maxHeight: { xs: '100%', md: '88vh' },
          m: { xs: 0, md: 2 },
        },
      }}>
      <DialogTitle sx={{ flexShrink: 0, pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            {t('assistantsPage.modals.view.title')}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label={t('assistantsPage.modals.close')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ px: { xs: 1, sm: 3 }, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Tab label={t('assistantsPage.modals.tabs.general', { defaultValue: 'General' })} />
        <Tab label={t('assistantsPage.modals.tabs.taskFlow', { defaultValue: 'Task Flow' })} />
      </Tabs>

      <DialogContent
        dividers
        sx={{
          p: 0,
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            display: tab === 0 ? 'block' : 'none',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: { xs: 2, sm: 3 },
          }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                {t('assistantsPage.modals.view.nameLabel')}
              </Typography>
              <TextField
                fullWidth
                value={profile.name}
                variant="outlined"
                slotProps={{ input: { readOnly: true } }}
                className={cx(classes.filledField, classes.readOnlyField)}
              />
            </Box>

            <BorderedGrid sx={{ p: 0, overflow: 'hidden' }}>
              <Accordion
                disableGutters
                elevation={0}
                expanded={openingLineExpanded}
                onChange={(_, expanded) => setOpeningLineExpanded(expanded)}
                sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="view-agent-profile-opening-line-content"
                  id="view-agent-profile-opening-line-header"
                  sx={{ minHeight: 72, px: 2.5, '& .MuiAccordionSummary-content': { mr: 1.5 } }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%">
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('assistantsPage.modals.openingLine.label')}
                    </Typography>
                    <FormControlLabel
                      sx={{ mr: 0 }}
                      onClick={(event) => event.stopPropagation()}
                      control={
                        <CustomSwitch
                          checked={!!profile.firstMessageInterruptionsEnabled}
                          disabled
                          inputProps={{
                            'aria-label': t(
                              'assistantsPage.modals.firstMessageInterruptionsEnabled.label',
                            ),
                          }}
                        />
                      }
                      label=""
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails
                  id="view-agent-profile-opening-line-content"
                  sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    value={profile.firstMessage ?? ''}
                    placeholder={t('assistantsPage.modals.openingLine.placeholder')}
                    variant="outlined"
                    slotProps={{ input: { readOnly: true } }}
                    className={cx(classes.filledField, classes.readOnlyField)}
                  />
                </AccordionDetails>
              </Accordion>
            </BorderedGrid>

            <Box sx={{ pb: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('assistantsPage.modals.view.promptLabel')}
                </Typography>
                {profile.prompt ? (
                  <Button
                    type="button"
                    size="small"
                    variant="text"
                    sx={{ minWidth: 'auto', px: 1, flexShrink: 0 }}
                    startIcon={showRaw ? <IconEye size={16} /> : <IconCode size={16} />}
                    onClick={() => setShowRaw((previous) => !previous)}>
                    {showRaw
                      ? t('assistantsPage.modals.view.showMarkdown', {
                          defaultValue: 'Show Markdown',
                        })
                      : t('assistantsPage.modals.view.showRaw', { defaultValue: 'Show Raw' })}
                  </Button>
                ) : null}
              </Stack>

              {profile.prompt ? (
                <Box className={classes.promptBox}>
                  {showRaw ? (
                    <Box className={classes.promptBoxRaw}>{profile.prompt}</Box>
                  ) : (
                    <MarkdownContent content={profile.prompt} variant="embedded" sx={{ p: 2 }} />
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('assistantsPage.modals.view.noPrompt')}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            display: tab === 1 ? 'flex' : 'none',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: { xs: 1, sm: 3 },
          }}>
          {tab === 1 && (
            <TaskFlowPanel
              initialTaskFlow={profile.taskFlow}
              readOnly
              profileName={profile.name}
              onSave={async (taskFlow) => taskFlow}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions className={classes.modalActions} sx={{ flexShrink: 0 }}>
        <Button type="button" onClick={handleClose} variant="contained">
          {t('assistantsPage.modals.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
